import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Progress } from './ui/progress';
import { 
  Plus, Calendar, Building2, Briefcase, Phone, Mail, Users, 
  CheckCircle, Clock, XCircle, FileText, Image, Trash2, Pencil,
  TrendingUp, Target, Award, Loader2, Timer, AlertTriangle,
  History, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPES_DEMARCHE = [
  { value: 'visite_entreprise', label: 'Visite en entreprise (Porte à porte)', icon: Building2, color: 'bg-blue-100 text-blue-700' },
  { value: 'envoi_cv', label: 'Envoi de CV (Email/Site web)', icon: Mail, color: 'bg-purple-100 text-purple-700' },
  { value: 'appel_telephonique', label: 'Appel téléphonique (Relance)', icon: Phone, color: 'bg-green-100 text-green-700' },
  { value: 'entretien', label: 'Entretien d\'embauche / Stage', icon: Users, color: 'bg-amber-100 text-amber-700' },
  { value: 'inscription_interim', label: 'Inscription Agence Intérim', icon: Briefcase, color: 'bg-cyan-100 text-cyan-700' }
];

const RESULTATS = [
  { value: 'candidature_deposee', label: 'Candidature déposée', icon: FileText, color: 'bg-blue-100 text-blue-700' },
  { value: 'entretien_decroche', label: 'Entretien décroché', icon: Award, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'en_attente', label: 'En attente de réponse', icon: Clock, color: 'bg-amber-100 text-amber-700' },
  { value: 'refus', label: 'Refus (motif si connu)', icon: XCircle, color: 'bg-red-100 text-red-700' }
];

// Fonction pour obtenir le lundi de la semaine (en heure locale)
const getMondayOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  // Utiliser les méthodes locales au lieu de toISOString() qui est en UTC
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const dayOfMonth = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${dayOfMonth}`;
};

const JournalDemarches = ({ apprenantNom = 'Bénéficiaire' }) => {
  const [demarches, setDemarches] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingDemarche, setEditingDemarche] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(getMondayOfWeek(new Date()));
  const [uploading, setUploading] = useState(false);
  const [expandedHistorique, setExpandedHistorique] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    date_action: new Date().toISOString().split('T')[0],
    type_demarche: '',
    nom_entreprise: '',
    poste_vise: '',
    resultat: '',
    note: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedWeek]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [demarchesRes, statsRes] = await Promise.all([
        api.get(`/demarches?semaine_du=${selectedWeek}`),
        api.get('/demarches/stats/me').catch(() => ({ data: null }))
      ]);
      setDemarches(demarchesRes.data);
      if (statsRes.data) setStats(statsRes.data);
    } catch (error) {
      // Si l'endpoint stats/me n'existe pas, on calcule côté client
      try {
        const allDemarches = await api.get('/demarches');
        setDemarches(allDemarches.data.filter(d => d.semaine_du === selectedWeek));
      } catch (e) {
        toast.error('Erreur de chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date_action: new Date().toISOString().split('T')[0],
      type_demarche: '',
      nom_entreprise: '',
      poste_vise: '',
      resultat: '',
      note: ''
    });
    setEditingDemarche(null);
  };

  const handleSubmit = async () => {
    if (!formData.type_demarche || !formData.nom_entreprise || !formData.resultat) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const semaineDemarche = getMondayOfWeek(formData.date_action);
      const dataToSend = {
        ...formData,
        semaine_du: semaineDemarche
      };

      if (editingDemarche) {
        await api.put(`/demarches/${editingDemarche.id}`, dataToSend);
        toast.success('Démarche mise à jour');
      } else {
        await api.post('/demarches', dataToSend);
        toast.success('Démarche ajoutée avec succès !');
      }

      setShowAddDialog(false);
      resetForm();
      
      // Mettre à jour selectedWeek pour afficher la semaine de la démarche créée/modifiée
      if (semaineDemarche !== selectedWeek) {
        setSelectedWeek(semaineDemarche);
      } else {
        // Si même semaine, recharger les données
        loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette démarche ?')) return;
    
    try {
      await api.delete(`/demarches/${id}`);
      toast.success('Démarche supprimée');
      loadData();
    } catch (error) {
      toast.error('Erreur de suppression');
    }
  };

  const handleEdit = (demarche) => {
    setFormData({
      date_action: demarche.date_action,
      type_demarche: demarche.type_demarche,
      nom_entreprise: demarche.nom_entreprise,
      poste_vise: demarche.poste_vise,
      resultat: demarche.resultat,
      note: demarche.note || ''
    });
    setEditingDemarche(demarche);
    setShowAddDialog(true);
  };

  const handleUploadPreuve = async (demarcheId, file) => {
    if (!file) return;
    setUploading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/demarches/${demarcheId}/preuve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      
      toast.success('Preuve téléversée');
      loadData();
    } catch (error) {
      toast.error('Erreur de téléversement');
    } finally {
      setUploading(false);
    }
  };

  const getTypeInfo = (type) => TYPES_DEMARCHE.find(t => t.value === type) || { label: type, color: 'bg-gray-100 text-gray-700', icon: FileText };
  const getResultatInfo = (resultat) => RESULTATS.find(r => r.value === resultat) || { label: resultat, color: 'bg-gray-100 text-gray-700', icon: Clock };

  // Générer les semaines disponibles (4 semaines passées + semaine courante)
  const getWeekOptions = () => {
    const weeks = [];
    const today = new Date();
    for (let i = -4; i <= 0; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + (i * 7));
      const monday = getMondayOfWeek(d);
      
      // Parser la date comme locale, pas UTC
      const [year, month, day] = monday.split('-').map(Number);
      const mondayDate = new Date(year, month - 1, day);
      const sunday = new Date(year, month - 1, day + 6);
      
      weeks.push({
        value: monday,
        label: `Semaine du ${mondayDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} au ${sunday.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`
      });
    }
    return weeks.reverse();
  };

  return (
    <div className="space-y-6">
      {/* Barre de progression vers les 60h */}
      {stats && (
        <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 rounded-3xl overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className="w-6 h-6 text-emerald-600" />
                <span className="font-bold text-emerald-900">Objectif 60h de recherche active</span>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700 px-3 py-1 text-lg font-bold">
                {stats.heures_cumulees}h / {stats.objectif_heures}h
              </Badge>
            </div>
            <Progress value={stats.pourcentage_objectif} className="h-3 bg-emerald-100" />
            <div className="flex items-center justify-between mt-2 text-sm">
              <span className="text-emerald-700">
                <Timer className="w-4 h-4 inline mr-1" />
                1 démarche = 30 min comptabilisées
              </span>
              <span className="text-emerald-600 font-medium">{stats.pourcentage_objectif}% atteint</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Statistiques de la semaine (info uniquement, plus de limite) */}
      {stats && (
        <Card className="rounded-3xl overflow-hidden bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-semibold text-blue-900">
                    Cette semaine : {stats.cette_semaine} démarche(s)
                  </p>
                  <p className="text-sm text-blue-700">
                    Temps comptabilisé : {stats.temps_recherche_semaine_minutes} min ({(stats.temps_recherche_semaine_minutes / 60).toFixed(1)}h)
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* En-tête */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 rounded-3xl overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="text-2xl text-amber-900 flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                <FileText className="w-7 h-7" />
                Mon Journal de Démarches
              </CardTitle>
              <p className="text-amber-700 mt-1">Bénéficiaire : <strong>{apprenantNom}</strong></p>
            </div>
            <Button 
              onClick={() => { resetForm(); setShowAddDialog(true); }}
              className="bg-amber-600 hover:bg-amber-700 rounded-xl shadow-lg"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle démarche
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-600" />
              <Select value={selectedWeek} onValueChange={setSelectedWeek}>
                <SelectTrigger className="w-72 rounded-xl bg-white">
                  <SelectValue placeholder="Sélectionner une semaine" />
                </SelectTrigger>
                <SelectContent>
                  {getWeekOptions().map(week => (
                    <SelectItem key={week.value} value={week.value}>{week.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Badge entretiens décrochés (si applicable) */}
            {demarches.filter(d => d.resultat === 'entretien_decroche').length > 0 && (
              <div className="ml-auto">
                <Badge className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-xl">
                  <Award className="w-4 h-4 mr-1" />
                  {demarches.filter(d => d.resultat === 'entretien_decroche').length} entretien(s) décroché(s)
                </Badge>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Liste des démarches */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      ) : demarches.length === 0 ? (
        <Card className="bg-white border-slate-200 rounded-3xl p-8 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">Aucune démarche enregistrée cette semaine</p>
          <p className="text-sm text-slate-400 mt-1">Cliquez sur "Nouvelle démarche" pour commencer</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {demarches.map((demarche) => {
            const typeInfo = getTypeInfo(demarche.type_demarche);
            const resultatInfo = getResultatInfo(demarche.resultat);
            const TypeIcon = typeInfo.icon;
            const ResultatIcon = resultatInfo.icon;

            return (
              <Card key={demarche.id} className="bg-white border-slate-200 rounded-3xl shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    {/* Icône type */}
                    <div className={`p-3 rounded-2xl ${typeInfo.color} flex-shrink-0`}>
                      <TypeIcon className="w-6 h-6" />
                    </div>
                    
                    {/* Infos principales */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                          <h3 className="font-bold text-lg text-slate-900">{demarche.nom_entreprise}</h3>
                          <p className="text-slate-600">{demarche.poste_vise}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${resultatInfo.color} px-3 py-1`}>
                            <ResultatIcon className="w-3 h-3 mr-1" />
                            {resultatInfo.label}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(demarche.date_action).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </span>
                        <Badge variant="outline" className="rounded-lg">
                          {typeInfo.label}
                        </Badge>
                      </div>
                      
                      {demarche.note && (
                        <p className="mt-3 text-sm text-slate-600 bg-slate-50 rounded-xl p-3">
                          💬 {demarche.note}
                        </p>
                      )}
                      
                      {/* Preuve */}
                      <div className="mt-3 flex items-center gap-3">
                        {demarche.preuve_url ? (
                          <Badge className="bg-green-100 text-green-700">
                            <Image className="w-3 h-3 mr-1" />
                            Preuve ajoutée
                          </Badge>
                        ) : (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleUploadPreuve(demarche.id, e.target.files?.[0])}
                            />
                            <Badge variant="outline" className="cursor-pointer hover:bg-slate-100">
                              <Image className="w-3 h-3 mr-1" />
                              {uploading ? 'Téléversement...' : 'Ajouter preuve (optionnel)'}
                            </Badge>
                          </label>
                        )}
                      </div>
                      
                      {/* Historique des mises à jour */}
                      {demarche.historique_statuts && demarche.historique_statuts.length > 0 && (
                        <div className="mt-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedHistorique(prev => ({
                              ...prev,
                              [demarche.id]: !prev[demarche.id]
                            }))}
                            className="text-xs text-slate-500 hover:text-slate-700 p-0 h-auto"
                          >
                            <History className="w-3 h-3 mr-1" />
                            Historique ({demarche.historique_statuts.length})
                            {expandedHistorique[demarche.id] ? (
                              <ChevronUp className="w-3 h-3 ml-1" />
                            ) : (
                              <ChevronDown className="w-3 h-3 ml-1" />
                            )}
                          </Button>
                          
                          {expandedHistorique[demarche.id] && (
                            <div className="mt-2 space-y-1 pl-4 border-l-2 border-amber-200">
                              {demarche.historique_statuts.slice().reverse().map((h, idx) => {
                                const ancienInfo = getResultatInfo(h.ancien_statut);
                                const nouveauInfo = getResultatInfo(h.nouveau_statut);
                                return (
                                  <div key={idx} className="text-xs text-slate-500">
                                    <span className="font-medium text-slate-700">
                                      {new Date(h.date).toLocaleString('fr-FR', {
                                        day: '2-digit', month: '2-digit', year: 'numeric',
                                        hour: '2-digit', minute: '2-digit'
                                      })}
                                    </span>
                                    {' - '}
                                    <span className="text-red-600">{ancienInfo?.label || h.ancien_statut}</span>
                                    {' → '}
                                    <span className="text-emerald-600">{nouveauInfo?.label || h.nouveau_statut}</span>
                                    <span className="text-slate-400"> par {h.modifie_par}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(demarche)}
                        className="text-slate-500 hover:text-blue-600"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(demarche.id)}
                        className="text-slate-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog Ajouter/Modifier */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-xl rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              <Plus className="w-5 h-5 text-amber-600" />
              {editingDemarche ? 'Modifier la démarche' : 'Nouvelle démarche'}
            </DialogTitle>
            <DialogDescription>
              Enregistrez vos actions de recherche d'emploi
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Date */}
            <div className="space-y-2">
              <Label>Date de l'action *</Label>
              <Input
                type="date"
                value={formData.date_action}
                onChange={(e) => setFormData({ ...formData, date_action: e.target.value })}
                className="rounded-xl"
              />
            </div>

            {/* Type de démarche */}
            <div className="space-y-2">
              <Label>Type de démarche *</Label>
              <Select 
                value={formData.type_demarche} 
                onValueChange={(v) => setFormData({ ...formData, type_demarche: v })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Sélectionner le type" />
                </SelectTrigger>
                <SelectContent>
                  {TYPES_DEMARCHE.map(type => {
                    const Icon = type.icon;
                    return (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Nom entreprise */}
            <div className="space-y-2">
              <Label>Nom de l'entreprise *</Label>
              <Input
                value={formData.nom_entreprise}
                onChange={(e) => setFormData({ ...formData, nom_entreprise: e.target.value })}
                placeholder="Ex: Super U Sainte-Marie"
                className="rounded-xl"
              />
            </div>

            {/* Poste visé */}
            <div className="space-y-2">
              <Label>Poste visé *</Label>
              <Input
                value={formData.poste_vise}
                onChange={(e) => setFormData({ ...formData, poste_vise: e.target.value })}
                placeholder="Ex: Hôte de caisse"
                className="rounded-xl"
              />
            </div>

            {/* Résultat */}
            <div className="space-y-2">
              <Label>Résultat actuel *</Label>
              <Select 
                value={formData.resultat} 
                onValueChange={(v) => setFormData({ ...formData, resultat: v })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Sélectionner le résultat" />
                </SelectTrigger>
                <SelectContent>
                  {RESULTATS.map(resultat => {
                    const Icon = resultat.icon;
                    return (
                      <SelectItem key={resultat.value} value={resultat.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {resultat.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Note */}
            <div className="space-y-2">
              <Label>Note / Commentaire</Label>
              <Textarea
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder="Ex: Le manager était absent, je dois rappeler mardi matin."
                className="rounded-xl resize-none"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button onClick={handleSubmit} className="rounded-xl bg-amber-600 hover:bg-amber-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              {editingDemarche ? 'Mettre à jour' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JournalDemarches;
